import { useCallback, useEffect, useRef, useState } from 'react';
import { nativeCall, nativeCallFailureMessage, type NativeCallRetirement, type NativeListener } from './nativeBridge';

/** Native retains proofs; this hook holds only sanitized, process-local metadata. */
export function useNativeGrantRetirements(enabled: boolean) {
  const [records, setRecords] = useState<NativeCallRetirement[]>([]);
  const [error, setError] = useState('');
  const current = useRef<NativeCallRetirement[]>([]);
  const mounted = useRef(true);
  const revision = useRef(0);
  const readSequence = useRef(0);
  const appliedRead = useRef(0);
  const versions = useRef(new Map<string, number>());
  const update = useCallback((record: NativeCallRetirement) => {
    versions.current.set(record.operationId, ++revision.current);
    current.current = [...current.current.filter(item => item.operationId !== record.operationId), record];
    if (mounted.current) setRecords(current.current);
  }, []);
  const refresh = useCallback(async () => {
    const before = revision.current;
    const request = ++readSequence.current;
    const snapshot = await nativeCall.getRetirements();
    if (request < appliedRead.current) return current.current;
    appliedRead.current = request;
    // Events received during the read are newer than its captured snapshot.
    const changed = current.current.filter(record => (versions.current.get(record.operationId) || 0) > before);
    const changedIds = new Set(changed.map(record => record.operationId));
    current.current = [...snapshot.filter(record => !changedIds.has(record.operationId)), ...changed];
    const retained = new Set(current.current.map(record => record.operationId));
    versions.current.forEach((_, operationId) => { if (!retained.has(operationId)) versions.current.delete(operationId); });
    if (mounted.current) { setRecords(current.current); setError(''); }
    return current.current;
  }, []);
  useEffect(() => {
    mounted.current = true;
    if (!enabled) return () => { mounted.current = false; };
    let active = true;
    let handle: NativeListener | undefined;
    void nativeCall.listenRetirements(record => { if (active) update(record); }).then(value => {
      if (active) handle = value; else void value.remove();
      // Read after listener attachment so an intervening Leave cannot be missed.
      if (active) void refresh().catch(() => { if (active) setError('Unable to read meeting access status. Retry checking access below.'); });
    }).catch(() => {
      if (active) setError('Unable to follow meeting access status. Retry checking access below.');
    });
    return () => { active = false; mounted.current = false; void handle?.remove().catch(() => undefined); };
  }, [enabled, refresh, update]);
  const mutate = useCallback(async (operationId: string, closeDormant: boolean) => {
    try {
      const before = revision.current;
      const result = await (closeDormant ? nativeCall.closeDormantAccess(operationId) : nativeCall.retryRetirement(operationId));
      if ((versions.current.get(operationId) || 0) <= before) update(result);
      if (mounted.current) setError('');
    }
    catch (failure) { if (mounted.current) setError(nativeCallFailureMessage(failure, 'Unable to close meeting access. Check your connection and retry closing access.')); }
  }, [update]);
  const check = useCallback(async () => {
    try { await refresh(); }
    catch { if (mounted.current) setError('Unable to read meeting access status. Retry checking access below.'); }
  }, [refresh]);
  const retry = useCallback((operationId: string) => mutate(operationId, false), [mutate]);
  const closeDormant = useCallback((operationId: string) => mutate(operationId, true), [mutate]);
  return { records, current, refresh, retry, closeDormant, check, error };
}
